/* eslint-disable no-useless-escape */
/* eslint-disable import/no-webpack-loader-syntax */

import React from "react";
import { Code, Link } from "rebass";
import Heading from "../components/Heading";
import Theory from "../components/Theory";
import Exercise from "../components/Exercise";
import Command from "../components/Command";
import P from "../components/P";
import SH from "../components/SH";
import Toc from "../components/Toc";
import ButtonLink from "../components/ButtonLink";

const Page = () => (
  <div>
    <Toc active="api" />
    <Heading fontSize={5}>Apis</Heading>
    <P>
      We have seen how to write websites that render HTML. How different is this
      from writing APIs?
    </P>
    <P>
      Place this controller in{" "}
      <Code>src/Controller/ApiMovieController.php</Code>
    </P>
    <P>
      And compare it side by side with our <Code>MovieController</Code>
    </P>.
    <SH code={require("raw-loader!./code/controller-api-movie.txt")} />
    <P>There are not many changes. Basically:</P>
    <ul>
      <li>
        We restrict routes to its HTTP method (something that we could do in the
        web version).
      </li>
      <li>We return meaningful HTTP Status codes.</li>
      <li>Instead of using templating, we serialize the values.</li>
    </ul>
    <P>
      Other parts of our code (Event Listeners, Services... remain untouched).
    </P>
    <P>This is another advantage of having lean Controllers.</P>
    <P>A big part of writing great APIs is dealing with serialization.</P>
    <P>
      In this case, for instance, we are saying that we are serializing
      properties that are in the group "movie". We can define these groups in
      our Entities.
    </P>
    <Theory>
      <P>Serializer for the win.</P>
    </Theory>
    <P>
      Install it with
      <SH
        language="bash"
        code={`
composer req serializer
        `}
      />
    </P>
    <P>
      Let's see the definition of groups in <Code>src/Entity/Movie.php</Code>
    </P>
    <SH code={require("raw-loader!./code/entity-movie-groups.txt")} />
    <P>
      And now in <Code>src/Entity/Actor.php</Code>
    </P>
    <SH code={require("raw-loader!./code/entity-actor-groups.txt")} />
    <P>Let's try our API:</P>
    <SH
      language="bash"
      code={`
curl -X GET http://localhost:8000/api/movies
        `}
    />
    <SH
      language="bash"
      code={`
curl -X GET http://localhost:8000/api/movies/1
        `}
    />
    <SH
      language="bash"
      code={`
curl -X POST http://localhost:8000/api/movies/1/sale \
-d '{
    "numTickets": 4,
    "fullName": "Barack Obama"
}'
        `}
    />
    <P>
      We could continue seeing examples of how to use the serializer. But let's
      see instead a powerful tool for building APIs.
    </P>
    <Heading fontSize={5}>Api Platform</Heading>
    <P>
      Appi Platform allows us to create very complete APIs with lots of useful
      tools requiring minimal configuration.
    </P>
    <P>To install it:</P>
    <Command>composer req api</Command>
    <P>
      As we have more routes, let's configure the routes of Api Platform so they
      are prefixed by "/apiplat":
    </P>
    <P>
      Edit <Code>config/routes/api_platform.yaml</Code>:
    </P>
    <SH
      language="yaml"
      code={`
api_platform:
    resource: .
    type: api_platform
    prefix:   '/apiplat'
`}
    />
    <P>
      We need to tell Api Platform which Entities are going to be used as
      resources of our API. Edit <Code>src/Entity/Movie.php</Code>:
    </P>
    <SH
      code={`
use ApiPlatform\\Core\\Annotation\\ApiResource;

/**
 * @ORM\\Entity(repositoryClass="App\\Repository\\MovieRepository")
 * @ORM\\Table(name="movie")
 * @ApiResource(attributes={"normalization_context"={"groups"={"movie"}}})
 */
class Movie
        `}
    />
    <P>
      And the same in <Code>src/Entity/Actor.php</Code>:
    </P>
    <SH
      code={`
use ApiPlatform\\Core\\Annotation\\ApiResource;

/**
 * @ORM\\Entity(repositoryClass="App\\Repository\\MovieRepository")
 * @ORM\\Table(name="actor")
 * @ApiResource(attributes={"normalization_context"={"groups"={"actor"}}})
 */
class Actor
        `}
    />
    <P>
      And that is it! Visit{" "}
      <Link href="http://localhost:8000/apiplat">
        http://localhost:8000/apiplat
      </Link>.
    </P>
    <Exercise>
      <P>
        Play around a bit with API Platform, testing the different endpoints
        that have been automatically created for you.
      </P>
    </Exercise>
    <P>
      Api Platform has created a fully functional Level 3 Restful API from the
      mapping of our entities, with documentation.
    </P>
    <P>
      Note however that real projects are much more than a CRUD, so you will
      still have to implement your own custom Controllers, Events, Services and
      so on, using the concepts from previous sections.
    </P>
    <ButtonLink to="/admin" title="Go to next section" />
  </div>
);

export default Page;



// WEBPACK FOOTER //
// ./src/pages/api.js